/**
 * AgentCore Strands stream processing
 *
 * Handles NDJSON streaming events from AgentCore Strands when agent_framework is agentcore_strands.
 * Events: contentBlockStart, contentBlockDelta, message, metadata, done, error
 */

// ============================================
// AgentCore Event Types
// ============================================

export interface ContentBlockStartEvent {
  type: 'contentBlockStart';
  contentBlockStart: {
    contentBlockIndex: number;
    start?: {
      toolUse?: {
        toolUseId: string;
        name: string;
      };
    };
  };
}

export interface ContentBlockDeltaEvent {
  type: 'contentBlockDelta';
  contentBlockDelta: {
    contentBlockIndex: number;
    delta: {
      text?: string;
      toolUse?: {
        input?: string;
      };
    };
  };
}

export interface MessageContent {
  text?: string;
  toolUse?: {
    toolUseId: string;
    name: string;
    input: unknown;
  };
  toolResult?: {
    toolUseId: string;
    content: unknown;
  };
}

export interface MessageEvent {
  type: 'message';
  message: {
    role: 'user' | 'assistant';
    content: MessageContent[];
  };
}

export interface MetadataEvent {
  type: 'metadata';
  metadata: {
    trace?: {
      orchestrationTrace?: {
        rationale?: { text: string };
        invocationInput?: unknown;
      };
    };
    usage?: {
      inputTokens: number;
      outputTokens: number;
    };
  };
}

export interface DoneEvent {
  type: 'done';
  id: string;
  created_at?: string;
  metadata?: {
    message_sent_time?: string;
    elapsed_time?: number;
    time_to_first_token?: number | null;
  };
  [key: string]: unknown;
}

export interface ErrorEvent {
  type: 'error';
  content: string;
}

export type AgentCoreEvent =
  | ContentBlockStartEvent
  | ContentBlockDeltaEvent
  | MessageEvent
  | MetadataEvent
  | DoneEvent
  | ErrorEvent;

// ============================================
// Trace shape for copilot (reasoning / toolUsage)
// ============================================

export interface AgentCoreTraceResult {
  type?: string;
  reasoning?: string;
  rationale?: string;
  toolUsage?: { function: string; parameters: Record<string, unknown> };
}

// ============================================
// Event Handlers Interface
// ============================================

export interface StreamEventHandlers {
  onContentBlockStart?: (event: ContentBlockStartEvent) => void;
  onContentBlockDelta?: (event: ContentBlockDeltaEvent) => void;
  onMessage?: (event: MessageEvent) => void;
  onMetadata?: (event: MetadataEvent) => void;
  onDone?: (event: DoneEvent) => void;
  onError?: (error: string, rawLine?: string) => void;
}

// ============================================
// Event Parser
// ============================================

function parseEventData(data: Record<string, unknown>): AgentCoreEvent | null {
  if (data['type'] === 'done') {
    return { ...data, type: 'done' } as DoneEvent;
  }
  if (data['contentBlockStart'] !== undefined) {
    return { ...data, type: 'contentBlockStart' } as ContentBlockStartEvent;
  }
  if (data['contentBlockDelta'] !== undefined) {
    return { ...data, type: 'contentBlockDelta' } as ContentBlockDeltaEvent;
  }
  if (data['message'] !== undefined) {
    return { ...data, type: 'message' } as MessageEvent;
  }
  if (data['type'] === 'metadata' || data['metadata'] !== undefined) {
    return {
      type: 'metadata',
      metadata: (data['metadata'] as MetadataEvent['metadata']) || data,
    } as MetadataEvent;
  }
  if (data['type'] === 'error') {
    return {
      type: 'error',
      content: (data['content'] as string) || 'Unknown error',
    };
  }
  return null;
}

/**
 * Parse a single line of NDJSON into an AgentCore event.
 * Handles direct events and wrapped format: { event: { ... } }.
 */
export function parseAgentCoreEvent(line: string): AgentCoreEvent | null {
  const trimmed = line.trim();
  if (!trimmed) return null;

  try {
    const data = JSON.parse(trimmed) as Record<string, unknown>;

    if (data['event'] !== undefined && typeof data['event'] === 'object') {
      const inner = data['event'] as Record<string, unknown>;
      return parseEventData(inner);
    }

    const event = parseEventData(data);
    if (event) return event;

    return null;
  } catch {
    return null;
  }
}

/**
 * Process a line and dispatch to the appropriate handler.
 */
export function processEventLine(line: string, handlers: StreamEventHandlers): void {
  const event = parseAgentCoreEvent(line);
  if (!event) return;

  switch (event.type) {
    case 'contentBlockStart':
      handlers.onContentBlockStart?.(event);
      break;
    case 'contentBlockDelta':
      handlers.onContentBlockDelta?.(event);
      break;
    case 'message':
      handlers.onMessage?.(event);
      break;
    case 'metadata':
      handlers.onMetadata?.(event);
      break;
    case 'done':
      handlers.onDone?.(event);
      break;
    case 'error':
      handlers.onError?.(event.content, line);
      break;
  }
}

// ============================================
// Stream Processor
// ============================================

export interface StreamProcessorParams {
  reader: ReadableStreamDefaultReader<Uint8Array>;
  onChunk?: (text: string) => void;
  onTrace?: (trace: AgentCoreTraceResult) => void;
  /** Called when the stream finishes. If the server sent a `done` event, it is passed (includes canonical `id`). Otherwise `undefined` (e.g. connection closed without `done`). */
  onComplete?: (done?: DoneEvent) => void;
  onError?: (error: string) => void;
}

export interface StreamProcessorResult {
  finalContent: string;
  isComplete: boolean;
}

function buildRationaleTrace(rationale: string): AgentCoreTraceResult {
  return {
    type: 'reasoning',
    reasoning: rationale,
    rationale: rationale,
  };
}

/**
 * Process an AgentCore Strands NDJSON stream.
 * Calls onChunk for text deltas, onTrace for tool/reasoning, onComplete/onError when done.
 */
export async function processAgentCoreStream(
  params: StreamProcessorParams
): Promise<StreamProcessorResult> {
  const { reader, onChunk, onTrace, onComplete, onError } = params;

  const decoder = new TextDecoder();
  let buffer = '';
  let finalContent = '';
  let isComplete = false;
  let hasStreamError = false;

  const toolUseMap = new Map<string, { toolUseId: string; name: string; input?: unknown }>();
  const contentBlockIndexToToolUseId = new Map<number, string>();
  const emittedToolUseIds = new Set<string>();
  let lastToolContentBlockIndex: number | undefined;

  function emitToolTraceIfReady(toolUseId: string): void {
    if (emittedToolUseIds.has(toolUseId)) return;
    const entry = toolUseMap.get(toolUseId);
    if (!entry) return;
    let parameters: Record<string, unknown> = {};
    if (entry.input !== undefined && entry.input !== '') {
      try {
        const parsed = typeof entry.input === 'string' ? JSON.parse(entry.input) : entry.input;
        parameters = typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : { raw: entry.input };
      } catch {
        parameters = { raw: entry.input };
      }
    }
    emittedToolUseIds.add(toolUseId);
    onTrace?.({
      type: 'tool',
      toolUsage: { function: entry.name, parameters },
    });
  }

  const handlers: StreamEventHandlers = {
    onContentBlockStart: (event) => {
      const { contentBlockIndex, start } = event.contentBlockStart;
      const toolUse = start?.toolUse;

      if (lastToolContentBlockIndex !== undefined) {
        const prevToolUseId = contentBlockIndexToToolUseId.get(lastToolContentBlockIndex);
        if (prevToolUseId) emitToolTraceIfReady(prevToolUseId);
      }

      if (toolUse) {
        lastToolContentBlockIndex = contentBlockIndex;
        contentBlockIndexToToolUseId.set(contentBlockIndex, toolUse.toolUseId);
        toolUseMap.set(toolUse.toolUseId, {
          toolUseId: toolUse.toolUseId,
          name: toolUse.name,
        });
      } else {
        lastToolContentBlockIndex = undefined;
      }
    },

    onContentBlockDelta: (event) => {
      const { contentBlockIndex, delta } = event.contentBlockDelta;
      const text = delta?.text;
      if (text) {
        finalContent += text;
        onChunk?.(text);
      }
      const toolInputDelta = delta?.toolUse?.input;
      if (toolInputDelta !== undefined) {
        const toolUseId = contentBlockIndexToToolUseId.get(contentBlockIndex);
        if (toolUseId) {
          const entry = toolUseMap.get(toolUseId);
          if (entry) {
            const prev = typeof entry.input === 'string' ? entry.input : '';
            const next = prev + (typeof toolInputDelta === 'string' ? toolInputDelta : String(toolInputDelta));
            toolUseMap.set(toolUseId, { ...entry, input: next });
          }
        }
      }
    },

    onMessage: (event) => {
      const message = event.message;
      if (message.role !== 'assistant') return;

      for (const content of message.content) {
        if (content.text) {
          if (content.text.length > finalContent.length) {
            const suffix = content.text.slice(finalContent.length);
            onChunk?.(suffix);
          }
          finalContent = content.text;
        }
        if (content.toolUse) {
          toolUseMap.set(content.toolUse.toolUseId, content.toolUse);
          emitToolTraceIfReady(content.toolUse.toolUseId);
        }
      }
    },

    onMetadata: (event) => {
      const trace = event.metadata.trace?.orchestrationTrace;
      if (trace?.rationale?.text) {
        onTrace?.(buildRationaleTrace(trace.rationale.text));
      }
    },

    onDone: (event) => {
      if (hasStreamError) return;
      if (lastToolContentBlockIndex !== undefined) {
        const toolUseId = contentBlockIndexToToolUseId.get(lastToolContentBlockIndex);
        if (toolUseId) emitToolTraceIfReady(toolUseId);
      }
      for (const [toolUseId] of toolUseMap) {
        emitToolTraceIfReady(toolUseId);
      }
      isComplete = true;
      onComplete?.(event);
    },

    onError: (error) => {
      hasStreamError = true;
      onError?.(error);
    },
  };

  try {
    while (true) {
      const { value, done } = await reader.read();

      if (done) {
        if (buffer.trim()) {
          processEventLine(buffer, handlers);
        }
        if (!hasStreamError && !isComplete) {
          isComplete = true;
          onComplete?.(undefined);
        }
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        if (line.trim()) {
          processEventLine(line, handlers);
        }
      }
      if (hasStreamError) break;
    }
  } catch (error) {
    console.error('Error reading AgentCore stream:', error);
    onError?.(error instanceof Error ? error.message : 'Stream read error');
    return { finalContent, isComplete: false };
  }

  return { finalContent, isComplete };
}
